// Invoices feature screen (task 3.6) — Req 6.3.
//
// Priced, itemised demands for payment, built from a job/quote. Wired to the
// Phase 2 invoices handler:
//   GET  /invoices?status=<status>   list by status
//   GET  /invoices/{id}              detail
//   POST /invoices                   create ({ clientId, quoteId?, jobId?, lines })
//   PUT  /invoices/{id}              edit lines / move status
//
// Money is computed server-side (NFR6.2): forms submit only line items; the detail
// screen shows the API's totals. On create, picking an accepted quote pre-fills the
// line editor from that quote (a manual "build invoice from the accepted quote"
// convenience — AI reconciliation is Phase 4). A new invoice is always a draft;
// status then advances toward paid (Req 6.5). The list queries each status and
// groups the results.

import { api } from '../api.js';
import {
  h, mount, runAsync, emptyView, routeToHash,
  screenEl, card, listRow, detailList, sectionHeading, muted,
  field, makeSelect, makeTextarea, errorSlot, setError,
  button, linkButton, buttonRow, runAction, goTo,
  statusBadge, shortDate, linesEditor, totalsBlock, moneyFromPence,
  INVOICE_TRANSITIONS, STATUS_ACTION_LABELS, packJobId,
  draftLinesEditor, draftLineToEditor, invoiceDraftSummary,
  removedLinesCard, modeToggle, takeDraftSeed, aiUnavailableNotice,
} from './ui.js';
import { quickbooksPushCard } from './quickbooks.js';
import { pdfDownloadCard } from './pdf.js';

const INVOICE_STATUSES = ['draft', 'pushed-to-QuickBooks', 'sent', 'paid'];
const invoiceHash = (id) => routeToHash({ section: 'invoices', action: 'view', id });

// --- List (grouped by status) ----------------------------------------------

export function invoicesListScreen() {
  const body = h('div');

  const load = () =>
    runAsync(
      body,
      async () => {
        const results = await Promise.all(INVOICE_STATUSES.map((s) => api.get(`/invoices?status=${encodeURIComponent(s)}`)));
        return INVOICE_STATUSES.map((status, i) => ({ status, items: (results[i] && results[i].items) || [] })).filter((g) => g.items.length);
      },
      (groups) => renderGroups(groups),
      { loadingMessage: 'Loading invoices…' },
    );

  const renderGroups = (groups) => {
    if (!groups.length) {
      return emptyView({ message: 'No invoices yet.', actionLabel: 'New invoice', onAction: () => goTo({ section: 'invoices', action: 'new' }) });
    }
    return h('div', {}, groups.map((g) =>
      h('div', { class: 'job-group' }, [
        h('div', { class: 'group-head' }, [statusBadge(g.status), h('span', { class: 'group-count', text: `${g.items.length}` })]),
        h('div', { class: 'list' }, g.items.map((inv) =>
          listRow({
            href: invoiceHash(inv.id),
            title: inv.ref,
            subtitle: shortDate(inv.updatedAt),
            meta: h('span', { class: 'list-row-amount', text: moneyFromPence(inv.totalPence) }),
          }),
        )),
      ]),
    ));
  };

  load();
  return screenEl('Invoices', body, {
    section: 'invoices',
    actions: linkButton('＋ New', routeToHash({ section: 'invoices', action: 'new' }), { variant: 'primary', small: true }),
  });
}

// --- Create ----------------------------------------------------------------

export function invoiceNewScreen() {
  const body = h('div');

  runAsync(
    body,
    () => Promise.all([api.get('/clients'), api.get('/quotes?status=accepted').catch(() => ({ items: [] }))]),
    ([clientsRes, quotesRes]) => renderNew((clientsRes && clientsRes.items) || [], (quotesRes && quotesRes.items) || []),
    { loadingMessage: 'Loading…' },
  );

  // AI reconciliation of an accepted quote vs. work done is the headline flow
  // (Req 6.1); a manual builder stays available. A "Draft from this job" action
  // elsewhere leaves a seed we pre-fill here.
  const renderNew = (clients, acceptedQuotes) => {
    if (!clients.length) {
      return emptyView({ message: 'Add a client before raising an invoice.', actionLabel: 'New client', onAction: () => goTo({ section: 'clients', action: 'new' }) });
    }
    const seed = takeDraftSeed('invoices');
    const holder = h('div');
    const toggle = modeToggle(
      [{ value: 'ai', label: '✨ AI draft' }, { value: 'manual', label: 'Manual' }],
      'ai',
      (mode) => mount(holder, mode === 'ai' ? aiForm() : manualForm()),
    );
    mount(holder, aiForm());
    return h('div', {}, [toggle, holder]);

    // Accepted-quote <option>s for a client; the label carries the quote total.
    // Declared as a hoisted function so aiForm() (a hoisted declaration called by
    // mount(holder, aiForm()) before this textual position) can reference it —
    // a const arrow here would be in its TDZ when aiForm runs.
    function quoteOptionsFor(clientId) {
      const forClient = acceptedQuotes.filter((q) => q.clientId === clientId);
      return [
        { value: '', label: forClient.length ? 'Select an accepted quote…' : 'No accepted quotes for this client' },
        ...forClient.map((q) => ({ value: q.id, label: `${q.ref} — ${moneyFromPence(q.totalPence)}` })),
      ];
    }

    // --- AI draft form: reconcile accepted quote vs. work done ---------------
    function aiForm() {
      const clientSelect = makeSelect(
        [{ value: '', label: 'Select a client…' }, ...clients.map((c) => ({ value: c.id, label: c.name }))],
        { value: (seed && seed.clientId) || '' },
      );
      const quoteField = h('div');
      const quoteRef = { el: makeSelect(quoteOptionsFor((seed && seed.clientId) || ''), { value: '' }) };
      mount(quoteField, quoteRef.el);

      const jobField = h('div');
      const jobRef = { el: makeSelect([{ value: '', label: 'No linked job' }], { value: '' }) };
      mount(jobField, jobRef.el);

      const refreshQuotes = () => {
        quoteRef.el = makeSelect(quoteOptionsFor(clientSelect.value), { value: '' });
        mount(quoteField, quoteRef.el);
      };
      const loadJobs = async (preselect) => {
        if (!clientSelect.value) {
          jobRef.el = makeSelect([{ value: '', label: 'No linked job' }], { value: '' });
          mount(jobField, jobRef.el);
          return;
        }
        try {
          const res = await api.get(`/clients/${encodeURIComponent(clientSelect.value)}/jobs`);
          const jobs = (res && res.items) || [];
          const opts = [{ value: '', label: 'No linked job' }, ...jobs.map((j) => ({ value: j.id, label: `${truncate(j.description, 40)} (${j.status})` }))];
          jobRef.el = makeSelect(opts, { value: preselect || '' });
          mount(jobField, jobRef.el);
        } catch {
          jobRef.el = makeSelect([{ value: '', label: 'No linked job' }], { value: '' });
          mount(jobField, jobRef.el);
        }
      };
      clientSelect.addEventListener('change', () => { refreshQuotes(); loadJobs(); });

      const descInput = makeTextarea({ rows: 3, placeholder: 'Anything that changed on the job (optional)…' });
      const err = errorSlot();
      const draftBtn = button('✨ Draft with AI', { variant: 'primary' });

      draftBtn.addEventListener('click', async () => {
        const clientId = clientSelect.value;
        const quoteId = quoteRef.el.value;
        if (!clientId) return setError(err, 'Please choose a client.');
        if (!quoteId) return setError(err, 'Pick the accepted quote to invoice from.');
        const payload = {
          clientId,
          quoteId,
          jobId: jobRef.el.value || undefined,
          description: descInput.value.trim() || undefined,
        };
        const result = await runAction(draftBtn, () => api.post('/invoices/draft', payload), { busyLabel: 'Drafting…', errSlot: err });
        if (result && result.invoice) mount(body, renderDraftReview(result));
      });

      if (seed && seed.clientId) loadJobs(seed.jobId);

      return h('div', { class: 'form' }, [
        muted('The app reconciles the accepted quote against the work done and drafts an invoice for you to review and edit. Nothing is pushed — you confirm everything.'),
        field('Client', clientSelect),
        h('div', { class: 'field' }, [h('label', { class: 'field-label', text: 'Accepted quote' }), quoteField]),
        h('div', { class: 'field' }, [h('label', { class: 'field-label', text: 'Linked job (optional)' }), jobField]),
        field('Notes on work done (optional)', descInput),
        err,
        buttonRow([draftBtn, linkButton('Cancel', routeToHash({ section: 'invoices' }))]),
      ]);
    }

    // --- Manual builder (unchanged behaviour) --------------------------------
    function manualForm() {
      const clientSelect = makeSelect([{ value: '', label: 'Select a client…' }, ...clients.map((c) => ({ value: c.id, label: c.name }))], { value: '' });
      const quoteSelect = makeSelect([{ value: '', label: 'No linked quote' }], { value: '' });
      const editor = linesEditor({ initial: [] });
      const editorHolder = h('div', {}, [editor.el]);
      let currentEditor = editor;
      const err = errorSlot();

      const quoteRef = { el: quoteSelect };
      const refreshQuoteOptions = () => {
        const forClient = acceptedQuotes.filter((q) => q.clientId === clientSelect.value);
        const opts = [{ value: '', label: forClient.length ? 'No linked quote' : 'No accepted quotes for this client' },
          ...forClient.map((q) => ({ value: q.id, label: `${q.ref} — ${moneyFromPence(q.totalPence)}` }))];
        const fresh = makeSelect(opts, { value: '' });
        fresh.addEventListener('change', onQuoteChange);
        quoteRef.el.replaceWith(fresh);
        quoteRef.el = fresh;
      };

      const onQuoteChange = () => {
        const q = acceptedQuotes.find((x) => x.id === quoteRef.el.value);
        const initial = q ? (q.lines || []).map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice })) : [];
        currentEditor = linesEditor({ initial });
        mount(editorHolder, currentEditor.el);
      };

      clientSelect.addEventListener('change', refreshQuoteOptions);

      const submit = button('Create invoice', { variant: 'primary' });
      submit.addEventListener('click', async () => {
        const clientId = clientSelect.value;
        const lines = currentEditor.lines();
        if (!clientId) return setError(err, 'Please choose a client.');
        if (!lines.length) return setError(err, 'Add at least one line with a description.');
        const payload = { clientId, quoteId: quoteRef.el.value || null, lines };
        const created = await runAction(submit, () => api.post('/invoices', payload), { busyLabel: 'Creating…', errSlot: err });
        if (created && created.id) goTo(invoiceHash(created.id));
      });

      return h('div', { class: 'form' }, [
        field('Client', clientSelect),
        h('div', { class: 'field' }, [h('label', { class: 'field-label', text: 'Linked quote' }), quoteRef.el]),
        sectionHeading('Lines'),
        editorHolder,
        err,
        buttonRow([submit, linkButton('Cancel', routeToHash({ section: 'invoices' }))]),
      ]);
    }

    // --- Editable review of the AI draft (Req 6.2, 6.3, NFR6.1) --------------
    function renderDraftReview(result) {
      const invoice = result.invoice;
      const editor = draftLinesEditor({ initial: (invoice.lines || []).map(draftLineToEditor), showChange: true });
      const err = errorSlot();
      const save = button('Save draft', { variant: 'primary' });
      const discard = button('Discard', {});
      discard.addEventListener('click', () => goTo({ section: 'invoices' }));
      save.addEventListener('click', async () => {
        const lines = editor.lines();
        if (!lines.length) return setError(err, 'Add at least one line with a description.');
        const updated = await runAction(save, () => api.put(`/invoices/${encodeURIComponent(invoice.id)}`, { lines }), { busyLabel: 'Saving…', errSlot: err });
        if (updated) goTo(invoiceHash(invoice.id));
      });

      return h('div', {}, [
        result.aiUnavailable ? aiUnavailableNotice(result.message) : null,
        card([
          muted(invoiceDraftSummary(result.summary)),
          muted('Added / changed / removed lines are marked against the accepted quote. Review and edit below — this is saved as a draft; pushing it to QuickBooks is a separate, explicit step.'),
        ], { title: `Draft ${invoice.ref}` }),
        card([editor.el], { title: 'Lines' }),
        removedLinesCard(result.removedLines),
        err,
        buttonRow([save, discard]),
      ]);
    }
  };

  return screenEl('New invoice', body, { section: 'invoices', backHref: routeToHash({ section: 'invoices' }), backLabel: 'Invoices' });
}

// --- Detail (view + edit lines + status transitions) -----------------------

export function invoiceDetailScreen(id) {
  const body = h('div');

  const refresh = () =>
    runAsync(
      body,
      async () => {
        const invoice = await api.get(`/invoices/${encodeURIComponent(id)}`);
        const client = await api.get(`/clients/${encodeURIComponent(invoice.clientId)}`).catch(() => null);
        return { invoice, client };
      },
      ({ invoice, client }) => renderView(invoice, client),
      { loadingMessage: 'Loading invoice…' },
    );

  const renderView = (invoice, client) => {
    const wrap = h('div');
    const editBtn = button('Edit lines', {});
    editBtn.addEventListener('click', () => mount(body, renderEdit(invoice)));

    const actionErr = errorSlot();
    const transitions = (INVOICE_TRANSITIONS[invoice.status] || []).map((next) => {
      const b = button(STATUS_ACTION_LABELS[next] || next, { variant: next === 'paid' ? 'primary' : 'secondary' });
      b.addEventListener('click', async () => {
        await runAction(b, () => api.put(`/invoices/${encodeURIComponent(id)}`, { status: next }), { busyLabel: 'Saving…', errSlot: actionErr });
        refresh();
      });
      return b;
    });

    mount(wrap, h('div', {}, [
      card([
        detailList([
          ['Reference', invoice.ref],
          ['Client', client ? client.name : invoice.clientId],
          ['Status', statusBadge(invoice.status)],
          ['From quote', invoice.quoteId ? linkButton('View quote', routeToHash({ section: 'quotes', action: 'view', id: invoice.quoteId }), { small: true }) : null],
          ['Job', invoice.jobId ? linkButton('View job', routeToHash({ section: 'jobs', action: 'view', id: packJobId(invoice.clientId, invoice.jobId) }), { small: true }) : null],
          ['Updated', shortDate(invoice.updatedAt)],
        ]),
      ], { title: invoice.ref }),
      card([totalsBlock(invoice)], { title: 'Lines' }),
      buttonRow([editBtn]),
      // Download or share the rendered PDF on mobile (Req 8.3, task 6.2).
      pdfDownloadCard({ entity: 'invoice', doc: invoice }),
      // Confirm-then-push to QuickBooks as an Invoice (Req 7.5, 7.6, NFR6.1). A
      // successful push also advances the invoice to pushed-to-QuickBooks server-side.
      quickbooksPushCard({ entity: 'invoice', doc: invoice, onPushed: () => refresh() }),
      transitions.length ? sectionHeading('Move status') : null,
      transitions.length ? buttonRow(transitions) : muted('This invoice is paid.'),
      actionErr,
    ]));
    return wrap;
  };

  const renderEdit = (invoice) => {
    const editor = linesEditor({ initial: (invoice.lines || []).map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice })) });
    const err = errorSlot();
    const save = button('Save lines', { variant: 'primary' });
    const cancel = button('Cancel', {});
    cancel.addEventListener('click', () => refresh());
    save.addEventListener('click', async () => {
      const lines = editor.lines();
      if (!lines.length) return setError(err, 'Add at least one line with a description.');
      const updated = await runAction(save, () => api.put(`/invoices/${encodeURIComponent(id)}`, { lines }), { busyLabel: 'Saving…', errSlot: err });
      if (updated) refresh();
    });
    return h('div', { class: 'form' }, [sectionHeading('Edit lines'), editor.el, err, buttonRow([save, cancel])]);
  };

  refresh();
  return screenEl('Invoice', body, { section: 'invoices', backHref: routeToHash({ section: 'invoices' }), backLabel: 'Invoices' });
}

function truncate(text, max) {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
